import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import { useMemo, useState } from 'react'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { ErrorState } from '@/components/shared/ErrorState'
import type { SectorIncidentsBucket } from '@/hooks/compare'

const tooltipStyle = {
  background: 'var(--surface-elevated)',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  color: 'var(--text-primary)',
  fontSize: 12,
} as const

function isOtherBucket(bucket: Pick<SectorIncidentsBucket, 'sector' | 'label'>) {
  return (
    bucket.sector.trim().toLowerCase() === 'other' ||
    bucket.label.trim().toLowerCase() === 'other'
  )
}

export interface SectorIncidentHeatmapProps {
  data: SectorIncidentsBucket[]
  selectedSector: string
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

export function SectorIncidentHeatmap({
  data,
  selectedSector,
  isLoading,
  isError,
  onRetry,
}: SectorIncidentHeatmapProps) {
  const [includeOther, setIncludeOther] = useState(false)
  const hasOtherBucket = data.some(isOtherBucket)
  const hasOtherData = data.some(
    (bucket) => isOtherBucket(bucket) && bucket.count > 0,
  )
  const visibleData = useMemo(
    () => (includeOther ? data : data.filter((bucket) => !isOtherBucket(bucket))),
    [data, includeOther],
  )

  if (isLoading) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">Story Coverage by Sector</h3>
        <Skeleton className="h-[240px] w-full" />
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">Story Coverage by Sector</h3>
        <ErrorState title="Couldn't load sector incidents." onRetry={onRetry} />
      </Card>
    )
  }

  if (visibleData.length === 0 || visibleData.every((d) => d.count === 0)) {
    return (
      <Card data-testid="compare-sector-heatmap" className="p-4">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h3 className="text-sm font-semibold">Story Coverage by Sector</h3>
          {hasOtherBucket ? (
            <div className="flex items-center gap-2">
              <Switch
                id="story-sector-include-other"
                checked={includeOther}
                onCheckedChange={setIncludeOther}
              />
              <Label
                htmlFor="story-sector-include-other"
                className="text-xs text-muted-foreground"
              >
                Include Other
              </Label>
            </div>
          ) : null}
        </div>
        <p className="text-sm text-muted-foreground">
          {hasOtherData && !includeOther
            ? 'Only Other has story data for the selected period. Include Other to view it.'
            : 'No story data for the selected period.'}
        </p>
      </Card>
    )
  }

  return (
    <Card data-testid="compare-sector-heatmap" className="p-4">
      <div className="mb-1 flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold">Story Coverage by Sector</h3>
        {hasOtherBucket ? (
          <div className="flex items-center gap-2">
            <Switch
              id="story-sector-include-other"
              checked={includeOther}
              onCheckedChange={setIncludeOther}
            />
            <Label
              htmlFor="story-sector-include-other"
              className="text-xs text-muted-foreground"
            >
              Include Other
            </Label>
          </div>
        ) : null}
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Story counts across all IntelHub stories.{selectedSector !== 'all' ? ' Your sector is highlighted.' : ''}{' '}
        Grouped by article industry tags, not victim-sector incident claims.
      </p>
      <div className="h-[240px] w-full" aria-live="polite">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={visibleData} margin={{ top: 4, right: 8, left: 0, bottom: 40 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--chart-grid-stroke)"
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: 'var(--chart-axis-label)' }}
              stroke="var(--chart-axis-stroke)"
              angle={-35}
              textAnchor="end"
              interval={0}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11, fill: 'var(--chart-axis-label)' }}
              stroke="var(--chart-axis-stroke)"
              width={36}
            />
            <Tooltip
              cursor={{ fill: 'var(--chart-muted)', opacity: 0.1 }}
              contentStyle={tooltipStyle}
            />
            <Bar dataKey="count" radius={[2, 2, 0, 0]}>
              {visibleData.map((entry) => (
                <Cell
                  key={entry.sector}
                  fill={
                    selectedSector === 'all' || entry.sector === selectedSector
                      ? 'var(--cat-1)'
                      : 'var(--cat-2)'
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  )
}
